export const PHOTO_FILTERS = [
  { id: "normal", label: "Normal", extra: "none" },
  { id: "noir", label: "Noir", extra: "grayscale(1)" },
  { id: "fade", label: "Fade", extra: "contrast(0.9) saturate(0.7) brightness(1.05)" },
  { id: "warm", label: "Warm", extra: "sepia(0.25) saturate(1.15)" },
  { id: "cool", label: "Cool", extra: "hue-rotate(18deg) saturate(0.85)" },
  { id: "sharp", label: "Sharp", extra: "contrast(1.2) saturate(1.05)" },
] as const

export function photoFilterCss(
  filter: string,
  brightness = 100,
  contrast = 100,
  saturate = 100,
) {
  const preset = PHOTO_FILTERS.find((item) => item.id === filter)
  const extra = preset?.extra && preset.extra !== "none" ? ` ${preset.extra}` : ""
  return `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturate}%)${extra}`
}

import { isSeedUserId, SEED_CREATOR_ID } from "@/lib/profile"

export function isSeedPost(post?: { authorId?: string; ownerId?: string; creator?: string; userId?: string }) {
  if (!post) return false
  if (post.creator === SEED_CREATOR_ID) return true
  if (isSeedUserId(post.authorId) || isSeedUserId(post.ownerId) || isSeedUserId(post.userId)) return true
  return !post.authorId
}

export function formatEth(value: number) {
  if (!Number.isFinite(value)) return "0 ETH"
  const text = value >= 1 ? value.toFixed(3) : value.toFixed(4)
  return `${text.replace(/0+$/, "").replace(/\.$/, "")} ETH`
}

export function isVideoPost(post?: { mediaType?: string; videoUrl?: string }) {
  return post?.mediaType === "video" || Boolean(post?.videoUrl)
}

export function isOnChainImage(post?: { mediaType?: string; videoUrl?: string; onChain?: boolean }) {
  return !isVideoPost(post) && post?.onChain !== false
}

export function isActivePost(post?: { burned?: boolean; removedFromProfile?: boolean; chainStatus?: string }) {
  return !post?.burned && !post?.removedFromProfile && post?.chainStatus !== "burned"
}

export function isLivePost(post?: {
  burned?: boolean
  removedFromProfile?: boolean
  chainStatus?: string
  status?: string
  scheduledAt?: string
}) {
  if (!isActivePost(post)) return false
  if (post?.status === "draft") return false
  if (post?.status === "scheduled" && post.scheduledAt) {
    const at = new Date(post.scheduledAt).getTime()
    if (Number.isFinite(at) && at > Date.now()) return false
  }
  return true
}

export function mediaLabel(post?: { mediaType?: string; videoUrl?: string }) {
  return isVideoPost(post) ? "Video" : "Photo"
}

export function rankForYou<T extends { _id: string; authorId: string; createdAt: string; topic?: string; city?: string }>(
  posts: T[],
  likes: Array<{ postId: string }>,
  comments: Array<{ postId: string }>,
  prefs?: { interests?: string[]; city?: string },
) {
  const likeCounts = new Map<string, number>()
  const commentCounts = new Map<string, number>()
  likes.forEach((like) => likeCounts.set(like.postId, (likeCounts.get(like.postId) || 0) + 1))
  comments.forEach((comment) => commentCounts.set(comment.postId, (commentCounts.get(comment.postId) || 0) + 1))
  const interests = new Set((prefs?.interests || []).map((item) => item.toLowerCase()).filter(Boolean))
  const city = (prefs?.city || "").trim().toLowerCase()
  const scored = [...posts].sort((a, b) => {
    const recencyA = Date.now() - new Date(a.createdAt).getTime()
    const recencyB = Date.now() - new Date(b.createdAt).getTime()
    const topicBoostA = interests.size && a.topic && interests.has(String(a.topic).toLowerCase()) ? 18 : 0
    const topicBoostB = interests.size && b.topic && interests.has(String(b.topic).toLowerCase()) ? 18 : 0
    const cityBoostA = city && (a.city || "").toLowerCase() === city ? 12 : 0
    const cityBoostB = city && (b.city || "").toLowerCase() === city ? 12 : 0
    const scoreA = (likeCounts.get(a._id) || 0) * 4 + (commentCounts.get(a._id) || 0) * 3 + topicBoostA + cityBoostA - recencyA / 3600000
    const scoreB = (likeCounts.get(b._id) || 0) * 4 + (commentCounts.get(b._id) || 0) * 3 + topicBoostB + cityBoostB - recencyB / 3600000
    return scoreB - scoreA
  })
  const mixed: T[] = []
  const leftover = [...scored]
  while (leftover.length) {
    const lastAuthor = mixed[mixed.length - 1]?.authorId
    const idx = leftover.findIndex((post) => post.authorId !== lastAuthor)
    const pick = idx >= 0 ? leftover.splice(idx, 1)[0] : leftover.shift()
    if (pick) mixed.push(pick)
  }
  return mixed
}

export function postOwnerId(post?: { ownerId?: string; authorId?: string }) {
  return post?.ownerId || post?.authorId || ""
}

export function toPublicPost<T extends {
  ownerId?: string
  authorId?: string
  forSale?: boolean
  walletAddress?: string
  ownerWallet?: string
  creator?: string
  creatorEmail?: string
}>(post: T, viewerId?: string): T {
  const own = Boolean(viewerId && postOwnerId(post) === viewerId)
  const listed = Boolean(post.forSale)
  return {
    ...post,
    creator: own ? post.creator : undefined,
    creatorEmail: undefined,
    walletAddress: own || listed ? post.walletAddress || "" : "",
    ownerWallet: own || listed ? post.ownerWallet || "" : "",
  }
}

export function postRecordId(post?: { _id?: unknown; id?: unknown } | string | number | null) {
  if (post == null) return ""
  if (typeof post === "string" || typeof post === "number") return String(post).trim()
  const raw = post._id ?? post.id ?? ""
  if (raw && typeof raw === "object") {
    const oid = (raw as { $oid?: string }).$oid
    if (oid) return String(oid).trim()
    const text = typeof (raw as { toString?: () => string }).toString === "function" ? String(raw) : ""
    if (text && text !== "[object Object]") return text.trim()
    return ""
  }
  return String(raw || "").trim()
}

export function samePostId(a?: unknown, b?: unknown) {
  const left = postRecordId(a as { _id?: unknown; id?: unknown } | string | number | null)
  const right = postRecordId(b as { _id?: unknown; id?: unknown } | string | number | null)
  return Boolean(left) && left === right
}

export function postPath(post?: { _id?: unknown; id?: unknown } | string | number | null) {
  const id = postRecordId(post)
  return id ? `/p/${encodeURIComponent(id)}` : "/"
}

export function explorerTxUrl(txHash?: string) {
  if (!txHash) return ""
  return `https://robinhoodchain.blockscout.com/tx/${txHash}`
}

export function timeAgo(iso?: string) {
  if (!iso) return ""
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ""
  const delta = Date.now() - then
  const mins = Math.floor(delta / 60000)
  if (mins < 1) return "now"
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  return `${Math.floor(days / 7)}w`
}
